import type { Entity } from "../gen/livesoup/v1/realtime_pb";
import type { PlotInfo } from "../net/api";
import type { Presence } from "../state/stores";
import { colorFor, hexColor } from "./color";
import { hint } from "../state/touch";

export function presenceFromProto(p: { plotId: string; name: string; ownerName: string; consentMode: string; state: string }): Presence | null {
  if (p.state !== "inside" && p.state !== "joined") return null;
  return { plotId: p.plotId, name: p.name, ownerName: p.ownerName, consentMode: p.consentMode, state: p.state };
}

export function bannerText(p: Presence, drafts: Drafts = {}): string {
  const parts = [p.name, p.ownerName ? `主人 ${p.ownerName}` : "公共展示區"];
  if (p.consentMode === "join") parts.push(p.state === "joined" ? hint("已加入（F 退出）", "已加入（點「退出領地」）") : hint("按 F 加入", "點「加入領地」"));
  if (drafts[p.plotId]) parts.push("草稿預覽中（別人看到的還是 live）");
  return parts.join(" · ");
}

/** DraftUpdated contract: drop the plot's drafts; the replacements arrive as EntityAdded right after. */
export function dropDrafts(entities: Record<string, Entity>, plotId: string): Record<string, Entity> {
  const out: Record<string, Entity> = {};
  for (const [id, e] of Object.entries(entities)) if (!(e.stage === "draft" && e.plotId === plotId)) out[id] = e;
  return out;
}

/** Plots with a draft, by draft version: DraftUpdated sets it, version 0 forgets it. */
export type Drafts = Record<string, number>;

export function draftsAfter(drafts: Drafts, plotId: string, version: number): Drafts {
  const out = { ...drafts };
  if (version > 0) out[plotId] = version; else delete out[plotId];
  return out;
}

/** On connect the gateway sends a plot's draft rows without a DraftUpdated first, so a draft row also marks its plot. */
export function draftsSeen(drafts: Drafts, e: Entity): Drafts {
  return e.stage === "draft" && !drafts[e.plotId] ? { ...drafts, [e.plotId]: 1 } : drafts;
}

/** While an estate has a draft its live layout is not what the owner sees, so a hand placement would land unseen and break the agent's base_version. */
export function draftBlocksPlacing(plot: PlotInfo, drafts: Drafts): string | null {
  return plot.kind === "estate" && drafts[plot.id] ? "這塊領地有草稿預覽中：請 agent promote 或先捨棄草稿，再手動擺放" : null;
}

/** Closed polyline along the footprint edge, every `step` metres, 0.15 m above the terrain. */
export function outlinePoints(bounds: { min: [number, number]; max: [number, number] }, height: (x: number, z: number) => number, step = 2): [number, number, number][] {
  const [x0, z0] = bounds.min, [x1, z1] = bounds.max;
  const corners: [number, number][] = [[x0, z0], [x1, z0], [x1, z1], [x0, z1], [x0, z0]];
  const pts: [number, number, number][] = [];
  for (let i = 0; i < 4; i++) {
    const [ax, az] = corners[i]!, [bx, bz] = corners[i + 1]!;
    const n = Math.max(1, Math.ceil(Math.hypot(bx - ax, bz - az) / step));
    for (let k = 0; k < n; k++) {
      const x = ax + ((bx - ax) * k) / n, z = az + ((bz - az) * k) / n;
      pts.push([x, height(x, z) + 0.15, z]);
    }
  }
  pts.push([x0, height(x0, z0) + 0.15, z0]);
  return pts;
}

export function boundaryColor(plot: PlotInfo): string {
  return plot.kind === "estate" && plot.owner_id ? hexColor(colorFor(plot.owner_id)) : "#ffffff";
}
